'use client'

import React, { useEffect } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button, ErrorState } from '@kiao-voice/ui-kit'
import { useTranslation } from 'react-i18next'
import { reportWebException } from '@/lib/error-reporting'

/**
 * Error boundary for the signed-in member area — account, billing, team,
 * transcripts, dictionaries, templates, API keys.
 *
 * Placed at the group root so the `ConnectedAppShell` sidebar survives the
 * error: a member whose transcript page just blew up can still reach the rest
 * of their account instead of landing on a blank 500. These pages already load
 * the Sentry SDK eagerly (`<ErrorReportingPreload>` in this group's layout), so
 * a report from here arrives with the breadcrumbs that led to it — which is
 * exactly the case the preload exists for.
 *
 * What it does NOT catch: a throw from `app/layout.tsx` itself — the shell, the
 * confirm provider, the font setup. `error.tsx` wraps the segment's page and
 * everything nested below it but renders inside that layout, so the layout's
 * own failure goes to `app/global-error.tsx`.
 *
 * `retry()` rather than `reset()`: these pages fetch from the API on mount, and
 * the failures worth offering a button for are transient ones — a dropped
 * request, a 502. Re-fetching is the recovery; clearing the boundary's state
 * without re-fetching would just re-render the same broken screen.
 */
export default function AppGroupError({
  error,
  retry,
}: {
  error: Error & { digest?: string }
  retry: () => void
}) {
  const { t } = useTranslation('web')

  useEffect(() => {
    reportWebException(error, { boundary: 'app', digest: error.digest })
  }, [error])

  return (
    <ErrorState
      icon={<AlertCircle aria-hidden="true" />}
      title={t('errorBoundary.title')}
      description={t('errorBoundary.description')}
      code={error.digest}
      action={
        <Button onClick={() => retry()}>
          <RefreshCw aria-hidden="true" width={15} height={15} />
          {t('errorBoundary.retry')}
        </Button>
      }
    />
  )
}
